import { isEmpty, mapValues, some } from 'lodash';
import gettext from 'js-utils/gettext';
import {
    CL_LOCALE,
    CO_LOCALE,
    formatMajorMoney,
    formatNumber,
    PE_LOCALE,
} from 'js-utils/intl';
import {
    CUSTOM_PRICING,
    INCLUDED_TEXT,
    LOCALES_WITH_FEES_SWITCHED,
    LOCALES_WITH_TAX_LABEL,
    PACKAGE_ONE,
    PACKAGE_THREE,
    PACKAGE_TWO,
    PACKAGE_TWO_ADDON,
    FREE_ITEM_PRICE,
} from '../constants/main';

const TRAILING_ZERO_REGEX = /((\.0*)|0)(?![0-9])/g;
const _trimZeros = (percent) => percent.replace(TRAILING_ZERO_REGEX, '');

const _getformattedPercentWithFixed = (fixedFee, percentFee, locale) => {
    const fees = [
        ...(percentFee ? [percentFee] : []),
        ...(fixedFee ? [fixedFee] : []),
    ];

    if (LOCALES_WITH_FEES_SWITCHED.includes(locale)) {
        fees.reverse();
    }

    const result = fees.join(' + ');

    return result ? gettext(result, { percentFee, fixedFee }) : FREE_ITEM_PRICE;
};

// Note: This formatting logic is currently duplicated in django/ebapps/landing_pages/modules/pricing.py
// TODO: Centralize this logic https://jira.evbhome.com/browse/EB-123387
const _formatPricing = (
    currencyFormat,
    locale,
    { currency, feePercent, feeFixed },
) => {
    let result = null;
    let formattedFixedFee = null;
    let formattedPercentFee = null;

    // e.g. 1% + 0.99
    // return null if we don't get any pricing data
    if (feePercent) {
        const trimmedPercentFee = _trimZeros(
            formatNumber(feePercent, currencyFormat),
        );

        formattedPercentFee = gettext('%(trimmedPercentFee)s%', {
            trimmedPercentFee,
        });
    }

    if (feeFixed) {
        formattedFixedFee = formatMajorMoney(
            feeFixed,
            currency,
            currencyFormat,
        );
    }

    result = _getformattedPercentWithFixed(
        formattedFixedFee,
        formattedPercentFee,
        locale,
    );

    /* We are gonna use USD for these countries, as PayPal doesn't accept their currencies
        We should be explicit that they are gonna charge fees in US dollars */
    const usdExtraCountries = [CL_LOCALE, CO_LOCALE, PE_LOCALE];

    if (usdExtraCountries.includes(locale)) {
        result = result.replace('$', 'US$');
    }

    return result;
};

const _formatMaxFee = ({ currency, feeMax }, currencyFormat) => {
    const maxFee = formatMajorMoney(feeMax, currency, currencyFormat);
    let formattedMax = null;

    if (feeMax) {
        formattedMax = gettext('Max %(maxFee)s / ticket', { maxFee });
    }

    return formattedMax;
};

const _formatProcessingFee = (
    { feePaymentProcessing },
    locale,
    currencyFormat,
) => {
    // Assumption: missing values for the processing fee means it's free or "included"
    // as part of the plan.
    let formattedProcessingFee = null;

    if (feePaymentProcessing) {
        formattedProcessingFee = gettext('%(processingFee)s%', {
            processingFee: _trimZeros(
                formatNumber(feePaymentProcessing, currencyFormat),
            ),
        });
    } else if (locale === 'de_DE') {
        formattedProcessingFee = INCLUDED_TEXT;
    }

    return formattedProcessingFee;
};

const _populateAssortmentsTax = ({ percent }, locale) => {
    const taxRate = {
        [PACKAGE_ONE]: {
            taxDescription: '',
            needTaxDescription: false,
        },
        [PACKAGE_TWO]: {
            taxDescription: '',
            needTaxDescription: false,
        },
        [PACKAGE_THREE]: {
            taxDescription: '',
            needTaxDescription: false,
        },
    };
    const assortmentTax = LOCALES_WITH_TAX_LABEL[locale];

    if (percent && assortmentTax) {
        const formattedTax = gettext('(+%(percent)s% %(taxName)s)', {
            percent,
            taxName: assortmentTax.taxName,
        });

        taxRate[PACKAGE_ONE].taxDescription = formattedTax;
        taxRate[PACKAGE_ONE].needTaxDescription = true;
        taxRate[PACKAGE_TWO].taxDescription = formattedTax;
        taxRate[PACKAGE_TWO].needTaxDescription = true;
        taxRate[PACKAGE_THREE].needTaxDescription = true;
    }

    return taxRate;
};

const _populateAssortmentsPricing = (
    assortmentsPricing,
    currencyFormat,
    locale,
) => {
    let formattedAssortmentsPricing = {
        ...mapValues(
            assortmentsPricing,
            _formatPricing.bind(null, currencyFormat, locale),
        ),
        [PACKAGE_THREE]: CUSTOM_PRICING,
    };
    // a null price means we didn't get a rule from pricing data
    const hasInvalidPrice = some(
        formattedAssortmentsPricing,
        (pricing) => pricing === null,
    );

    if (hasInvalidPrice) {
        formattedAssortmentsPricing = {
            [PACKAGE_ONE]: null,
            [PACKAGE_TWO]: null,
            [PACKAGE_THREE]: null,
        };
    }

    return formattedAssortmentsPricing;
};

const _populateAssortmentsPricingPerItemType = (
    assortmentsPricing,
    currencyFormat,
    locale,
) => {
    const formattedPricesPerItemTypePerPackage = mapValues(
        assortmentsPricing,
        (itemType) => {
            return {
                ...mapValues(
                    itemType,
                    _formatPricing.bind(null, currencyFormat, locale),
                ),
                [PACKAGE_THREE]: CUSTOM_PRICING,
            };
        },
    );

    return formattedPricesPerItemTypePerPackage;
};

const _populateFeeBreakdown = (assortmentsPricing, locale, currencyFormat) => {
    const formattedPricingData = mapValues(
        assortmentsPricing,
        (packageRule) => {
            // EXPERIMENT_MARKER=LOW_ATV_CAPPED_FEE
            let cappedFeeFormatters = {};

            if (packageRule.cappedFeeRate && packageRule.cappedFeeThreshold) {
                const currencyFormatForThreshold = '¤#,##0';

                cappedFeeFormatters = {
                    formattedCappedFee: formatMajorMoney(
                        packageRule.cappedFeeRate,
                        packageRule.currency,
                        currencyFormat,
                    ),
                    formattedFeeThreshold: formatMajorMoney(
                        packageRule.cappedFeeThreshold,
                        packageRule.currency,
                        currencyFormatForThreshold,
                    ),
                };
            }

            return {
                formattedFee: _formatPricing(
                    currencyFormat,
                    locale,
                    packageRule,
                ),
                formattedMax: _formatMaxFee(packageRule, currencyFormat),
                formattedProcessingFee: _formatProcessingFee(
                    packageRule,
                    locale,
                    currencyFormat,
                ),
                ...cappedFeeFormatters,
            };
        },
    );
    const processingFee =
        formattedPricingData[PACKAGE_ONE].formattedProcessingFee;

    // Beyond Free Smokescreen Experiment
    // Package 2 Addon will be the same pricing data as package 2
    formattedPricingData[PACKAGE_TWO_ADDON] = formattedPricingData[PACKAGE_TWO];

    // Add in the Package 3 hardcoded data
    formattedPricingData[PACKAGE_THREE] = {
        formattedFee: CUSTOM_PRICING,
        formattedMax: null,
        formattedProcessingFee: processingFee,
    };

    return formattedPricingData;
};

const _populateFeeBreakdownPerItemType = (
    assortmentsPricing,
    locale,
    currencyFormat,
) => {
    let formattedPricingData = mapValues(assortmentsPricing, (itemType) => {
        return mapValues(itemType, (packageRule) => {
            // EXPERIMENT_MARKER=LOW_ATV_CAPPED_FEE
            let cappedFeeFormatters = {};

            if (packageRule.cappedFeeRate && packageRule.cappedFeeThreshold) {
                const currencyFormatForThreshold = '¤#,##0';

                cappedFeeFormatters = {
                    formattedCappedFee: formatMajorMoney(
                        packageRule.cappedFeeRate,
                        packageRule.currency,
                        currencyFormat,
                    ),
                    formattedFeeThreshold: formatMajorMoney(
                        packageRule.cappedFeeThreshold,
                        packageRule.currency,
                        currencyFormatForThreshold,
                    ),
                };
            }

            return {
                formattedFee: _formatPricing(
                    currencyFormat,
                    locale,
                    packageRule,
                ),
                formattedMax: _formatMaxFee(packageRule, currencyFormat),
                formattedProcessingFee: _formatProcessingFee(
                    packageRule,
                    locale,
                    currencyFormat,
                ),
                ...cappedFeeFormatters,
            };
        });
    });

    // set formattedProcessingFee for package 3 to be the same as each item type's package 1
    formattedPricingData = mapValues(formattedPricingData, (itemType) => ({
        ...itemType,
        [PACKAGE_THREE]: {
            formattedFee: CUSTOM_PRICING,
            formattedMax: null,
            formattedProcessingFee:
                itemType[PACKAGE_ONE].formattedProcessingFee,
        },
    }));

    return formattedPricingData;
};

const _hasFixedFee = (assortmentsPricing) =>
    // if PACKAGE_ONE has a fixedFee let's assume they all do
    !!assortmentsPricing[PACKAGE_ONE].feeFixed;

const _hasFixedFeePerItemType = (assortmentsPricing) =>
    // if PACKAGE_ONE has a fixedFee let's assume they all do
    !!assortmentsPricing['ticket'][PACKAGE_ONE].feeFixed;

const _checkAssortmentTax = (locale) =>
    !isEmpty(LOCALES_WITH_TAX_LABEL[locale]);

export const getInitialData = ({
    activeScopeUserId,
    assortmentsAvailability,
    assortmentsPricing,
    assortmentsTax,
    canAccessLegacyConvert,
    canChangePlan,
    crumb,
    eid,
    env: {
        defaultCurrency,
        currencyFormat,
        localeInfo: { locale, tld },
        ebDomain,
    },
    featureFlags,
    isCopy,
    isCoyoteCopy,
    isCreate,
    isFromAccount,
    isLegacy,
    packageTitles,
    referer,
    showLowAtvUi,
    currencySymbol,
    localeDefaultPlan,
    user,
    userId,
    userPlan,
    userType,
    gaSettings,
}) => {
    const shouldUseRefactoredPricingTable =
        featureFlags.useRefactoredPricingTableComparison;

    const populateAssortmentsPricing = shouldUseRefactoredPricingTable
        ? _populateAssortmentsPricingPerItemType
        : _populateAssortmentsPricing;

    const populateFeeBreakdown = shouldUseRefactoredPricingTable
        ? _populateFeeBreakdownPerItemType
        : _populateFeeBreakdown;

    const hasFixedFee = shouldUseRefactoredPricingTable
        ? _hasFixedFeePerItemType
        : _hasFixedFee;

    return {
        assortments: {
            assortmentsAvailability,
            assortmentsPricing: populateAssortmentsPricing(
                assortmentsPricing,
                currencyFormat,
                locale,
            ),
            assortmentsTax: _populateAssortmentsTax(assortmentsTax, locale),
            feeBreakdown: populateFeeBreakdown(
                assortmentsPricing,
                locale,
                currencyFormat,
            ),
            hasFixedFee: hasFixedFee(assortmentsPricing),
            packageTitles,
            hasAssortmentsTax: _checkAssortmentTax(locale),
        },
        env: {
            defaultCurrency,
            currencyFormat,
            domain: ebDomain,
            locale,
            tld,
            eid,
            crumb,
            referer,
        },
        featureFlags: {
            crazyEggFeatureFlag: featureFlags.crazyEggFeatureFlag,
            enablePricingCalculator: featureFlags.enablePricingCalculator,
            launchPackageEssentialV2: featureFlags.launchPackageEssentialV2,
            isPackageChangeDowngradeEnabled:
                featureFlags.isPackageChangeDowngradeEnabled,
            cappedFeeExperimentFeatureFlag:
                featureFlags.cappedFeeExperimentFeatureFlag,
            useRefactoredPricingTableComparison:
                featureFlags.useRefactoredPricingTableComparison,
        },
        user: {
            activeScopeUserId,
            canAccessLegacyConvert,
            canChangePlan,
            isAuthenticated: user.isAuthenticated,
            isCopy,
            isCoyoteCopy,
            isCreate,
            isFromAccount,
            isLegacy,
            userId,
            userPlan,
            userType,
        },
        gaSettings,
        showLowAtvUi,
        currencySymbol,
        localeDefaultPlan,
    };
};
